/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { theme } from '../../styles/theme'
import { useEffect, useRef } from 'react'
import { CalculatorData } from '../../types'
import * as d3 from 'd3'

interface CalculatorChartProps {
  data: CalculatorData[]
}

const chartContainerStyles = css`
  width: 100%;
  height: 400px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  background: white;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    height: 300px;
  }
`

const emptyStateStyles = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #6c757d;
  
  i {
    font-size: 3rem;
    margin-bottom: 1rem;
  }
`

export const CalculatorChart = ({ data }: CalculatorChartProps) => {
  const svgRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!data.length || !svgRef.current) return

    const container = d3.select(svgRef.current)
    container.selectAll("*").remove()
    
    const containerWidth = svgRef.current.getBoundingClientRect().width
    const isMobile = window.innerWidth < 768
    const shouldRotateLabels = data.length > 15
    const margin = isMobile 
      ? {top: 15, right: 15, bottom: shouldRotateLabels ? 50 : 35, left: 60}
      : {top: 20, right: 30, bottom: shouldRotateLabels ? 60 : 40, left: 90}
    const width = Math.max(250, containerWidth - margin.left - margin.right)
    const height = (isMobile ? 300 : 400) - margin.top - margin.bottom

    const svg = container.append("svg")
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom)

    const g = svg.append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`)

    const xScale = d3.scaleLinear()
      .domain([0, data.length - 1])
      .range([0, width])

    const yScale = d3.scaleLinear()
      .domain([0, d3.max(data, d => d.value) || 0])
      .range([height, 0])

    const line = d3.line<CalculatorData>()
      .x(d => xScale(d.year))
      .y(d => yScale(d.value))
      .curve(d3.curveMonotoneX)

    // Add axes
    const maxTicks = isMobile ? 5 : 8
    const xTicks = Math.min(maxTicks, data.length - 1)
    const yTicks = isMobile ? 4 : 6
    
    g.append("g")
      .attr("transform", `translate(0,${height})`)
      .call(d3.axisBottom(xScale)
        .ticks(xTicks)
        .tickFormat(d => d.toString()))
      .selectAll("text")
      .style("font-size", isMobile ? "9px" : "11px")
      .style("text-anchor", shouldRotateLabels ? "end" : "middle")
      .attr("transform", shouldRotateLabels ? "rotate(-45)" : null)
      .attr("dx", shouldRotateLabels ? "-0.8em" : "0")
      .attr("dy", shouldRotateLabels ? "0.15em" : "0")

    const formatYAxisValue = (value: number): string => {
      if (value >= 1000000000000) { // $1 trillion or more
        return `$${(value / 1000000000000).toFixed(1)}T`
      } else if (value >= 1000000000) { // $1 billion or more
        return `$${(value / 1000000000).toFixed(1)}B`
      } else if (value >= 1000000) { // $1 million or more
        return `$${(value / 1000000).toFixed(1)}M`
      } else {
        return `$${(value / 1000).toFixed(0)}K`
      }
    }

    g.append("g")
      .call(d3.axisLeft(yScale)
        .ticks(yTicks)
        .tickFormat(d => formatYAxisValue(d as number)))
      .selectAll("text")
      .style("font-size", isMobile ? "10px" : "12px")

    // Add line
    g.append("path")
      .datum(data)
      .attr("fill", "none")
      .attr("stroke", theme.colors.primary)
      .attr("stroke-width", 3)
      .attr("d", line)

    // Add dots
    g.selectAll(".dot")
      .data(data)
      .enter().append("circle")
      .attr("class", "dot")
      .attr("cx", d => xScale(d.year))
      .attr("cy", d => yScale(d.value))
      .attr("r", isMobile ? 3 : 4)
      .attr("fill", theme.colors.primary)

    // Add axis labels
    const bottomLabelY = shouldRotateLabels 
      ? (isMobile ? 45 : 55) 
      : (isMobile ? 30 : 35)
    
    g.append("text")
      .attr("x", width / 2)
      .attr("y", height + bottomLabelY)
      .style("text-anchor", "middle")
      .style("font-size", isMobile ? "11px" : "12px")
      .text("Year")

    g.append("text")
      .attr("transform", "rotate(-90)")
      .attr("x", -height / 2)
      .attr("y", isMobile ? -45 : -70)
      .style("text-anchor", "middle")
      .style("font-size", isMobile ? "11px" : "12px")
      .text("Account Value")
  }, [data])

  if (!data.length) {
    return (
      <div css={chartContainerStyles}>
        <div css={emptyStateStyles}>
          <i className="fas fa-chart-line"></i>
          <p>Chart will appear here after calculation</p>
        </div>
      </div>
    )
  }

  return (
    <div css={chartContainerStyles} ref={svgRef} />
  )
}